import React from 'react';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { ExtractedTermsFilterInput, FieldMetadataMap } from '../../types/extractedTerms';
import { getOperatorDisplayName } from '../../utils/extractedTermsFormatters';
import { ReactComponent as TrashIcon } from '../../shared/icons/trashcan-icon.svg';
import styles from './FilterRow.module.scss';

interface FilterRowProps {
  filter: ExtractedTermsFilterInput;
  fieldMetadata: FieldMetadataMap;
  onChange: (filter: ExtractedTermsFilterInput) => void;
  onRemove: () => void;
  canRemove: boolean;
}

export const FilterRow: React.FC<FilterRowProps> = ({
  filter,
  fieldMetadata,
  onChange,
  onRemove,
  canRemove,
}) => {
  const selectedFieldMetadata = filter.field ? fieldMetadata[filter.field] : null;
  const availableOperators = selectedFieldMetadata?.filter_operators || [];

  const handleFieldChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newField = e.target.value;
    const newMetadata = fieldMetadata[newField];

    // Reset operator and value when field changes
    onChange({
      field: newField,
      operator: newMetadata?.filter_operators[0] || 'equals',
      value: '',
    });
  };

  const handleOperatorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({
      ...filter,
      operator: e.target.value,
      // Reset value if switching to/from 'between' or 'in'
      value: (e.target.value === 'between' || e.target.value === 'in') ? [] : '',
    });
  };

  const handleValueChange = (value: any) => {
    onChange({
      ...filter,
      value,
    });
  };

  const renderValueInput = () => {
    if (!selectedFieldMetadata) {
      return (
        <input className="form-control"
          type="text"
          placeholder="Select a field first"
          disabled
        />
      );
    }

    // Handle 'between' operator (two inputs - number or date)
    if (filter.operator === 'between') {
      const values = Array.isArray(filter.value) ? filter.value : ['', ''];
      const inputType = (selectedFieldMetadata.type as string) === 'date' ? 'date' : 'number';
      const placeholderMin = inputType === 'date' ? 'Start Date' : 'Min';
      const placeholderMax = inputType === 'date' ? 'End Date' : 'Max';

      return (
        <div className={styles.betweenInputs}>
          <input className="form-control"
            type={inputType}
            placeholder={placeholderMin}
            value={values[0] || ''}
            onChange={(e) => handleValueChange([e.target.value, values[1]])}
          />
          <span className={styles.betweenSeparator}>to</span>
          <input className="form-control"
            type={inputType}
            placeholder={placeholderMax}
            value={values[1] || ''}
            onChange={(e) => handleValueChange([values[0], e.target.value])}
          />
        </div>
      );
    }

    // Handle 'in' operator (multi-select for enums)
    if (filter.operator === 'in' && selectedFieldMetadata.type === 'enum') {
      const selectedValues = Array.isArray(filter.value) ? filter.value : [];
      return (
        <select className="custom-select"
          multiple
          value={selectedValues}
          onChange={(e) => {
            const options = Array.from(e.target.selectedOptions);
            handleValueChange(options.map((opt) => opt.value));
          }}
        >
          {selectedFieldMetadata.enum_values?.map((enumValue) => (
            <option key={enumValue} value={enumValue}>
              {enumValue}
            </option>
          ))}
        </select>
      );
    }

    // Handle boolean type
    if (selectedFieldMetadata.type === 'boolean') {
      return (
        <select className="custom-select"
          value={String(filter.value)}
          onChange={(e) => handleValueChange(e.target.value === 'true')}
        >
          <option value="">Select...</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    }

    // Handle enum type (dropdown)
    if (selectedFieldMetadata.type === 'enum') {
      return (
        <select className="custom-select"
          value={filter.value}
          onChange={(e) => handleValueChange(e.target.value)}
        >
          <option value="">Select...</option>
          {selectedFieldMetadata.enum_values?.map((enumValue) => (
            <option key={enumValue} value={enumValue}>
              {enumValue}
            </option>
          ))}
        </select>
      );
    }

    // Handle number type
    if (selectedFieldMetadata.type === 'number') {
      return (
        <input className="form-control"
          type="number"
          placeholder="Enter value"
          value={filter.value || ''}
          onChange={(e) => handleValueChange(e.target.value)}
        />
      );
    }

    // Handle date type (HTML5 date picker)
    if ((selectedFieldMetadata.type as string) === 'date') {
      return (
        <input className="form-control"
          type="date"
          placeholder="Select date"
          value={filter.value || ''}
          onChange={(e) => handleValueChange(e.target.value)}
        />
      );
    }

    // Handle string type (default)
    return (
      <input className="form-control"
        type="text"
        placeholder="Enter value"
        value={filter.value || ''}
        onChange={(e) => handleValueChange(e.target.value)}
      />
    );
  };

  return (
    <Row className={styles.filterRow}>
      <Col md={4}>
        <select className="custom-select"
          value={filter.field || ''}
          onChange={handleFieldChange}
        >
          <option value="">Select Field...</option>
          {Object.entries(fieldMetadata)
            .sort(([, a], [, b]) => a.display_name.localeCompare(b.display_name))
            .map(([fieldName, metadata]) => (
              <option key={fieldName} value={fieldName}>
                {metadata.display_name}
              </option>
            ))}
        </select>
      </Col>
      <Col md={3}>
        <select className="custom-select"
          value={filter.operator || 'equals'}
          onChange={handleOperatorChange}
          disabled={!filter.field}
        >
          {availableOperators.map((op) => (
            <option key={op} value={op}>
              {getOperatorDisplayName(op, selectedFieldMetadata?.type as string)}
            </option>
          ))}
        </select>
      </Col>
      <Col md={4}>
        {renderValueInput()}
      </Col>
      <Col md={1} className={styles.removeButtonCol}>
        <Button
          variant="link"
          onClick={onRemove}
          disabled={!canRemove}
          className={styles.removeButton}
          title="Remove filter"
        >
          <TrashIcon />
        </Button>
      </Col>
    </Row>
  );
};
